<template>
  <base-slide-over ref="slideOver" :title="title" size="xl">
    <base-form ref="form" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid">
      <div>Plan</div>
      <base-advanced-select class="tw-mt-3" label="Plan" placeholder="Select plan" :options="planOptions" v-model="selectedPlans" label-key="name" multiple required />
      <base-date-picker label="Plan Start Date" placeholder="Select start date" v-model="planStartDate" required :disabled-dates="disabledDates" />
      <base-select
        label="Sign Up Location"
        placeholder="Select a location"
        :optgroups="locationOptGroups"
        v-model="selectedLocationId"
        label-key="name"
        value-key="businessLocationId"
        required
        v-if="locations.length > 1 || selectedLocationId === null"
      />
      <div>Card</div>
      <base-card class="tw-flex tw-flex-nowrap tw-mt-2" :class="{ 'tw-bg-primary-50': paymentOption === 'cardOnFile' }">
        <base-radio class="tw-mr-2" value="cardOnFile" name="paymentOption" v-model="paymentOption"></base-radio>
        <div class="tw-flex tw-justify-between tw-w-full">
          <div>
            <div class="tw-text-gray-800 tw-text-sm tw-font-normal">Card on file</div>
            <div class="tw-text-gray-500 tw-text-sm">Uses {{ organization.customerLabelSingular }}'s current default card on file.</div>
          </div>
          <div class="tw-flex tw-text-right">
            <credit-card-icon class="tw-w-6 tw-h-6 tw-my-auto" :name="savedCreditCard.brand" />
            <div class="tw-ml-3 tw-text-sm" v-if="savedCreditCard.last4">
              <div>**** {{ savedCreditCard.last4 }}</div>
              <div class="tw-text-gray-500 tw-text-xs">Exp {{ expDate(savedCreditCard.expMonth, savedCreditCard.expYear) }}</div>
            </div>
            <div class="tw-ml-3 tw-text-gray-500" v-else>No Card</div>
          </div>
        </div>
      </base-card>
      <base-card class="tw-flex tw-flex-nowrap tw-mt-2" :class="{ 'tw-bg-primary-50': paymentOption === 'manual' }">
        <base-radio class="tw-mr-2" value="manual" name="paymentOption" v-model="paymentOption"></base-radio>
        <div class="tw-flex tw-flex-col tw-w-full">
          <div class="tw-text-gray-800 tw-text-sm tw-font-normal">New card (replaces existing)</div>
          <base-credit-card-input class="tw-mt-3" ref="creditCardInput" hide-name hide-postal-code />
        </div>
      </base-card>
    </base-form>
    <inline-error class="tw-my-3" id="create-error" :title="errorTitle" :message="errorMessage" :error="error" />
    <template #fixed-bottom>
      <div>
        <div class="tw-text-sm tw-text-gray-700 tw-px-4 sm:tw-px-6">
          By clicking [Add Plan] below, the {{ organization.customerLabelSingular }} is agreeing to the
          <base-button type="text" @click="$refs.termsModal.show()">Terms of Service</base-button>.
        </div>
        <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
          <base-button type="flat" @click="$refs.slideOver.hide()" class="tw-mr-3">Cancel</base-button>
          <base-button @click="createPlan" :disabled="!isFormValid" :loading="isCreatingPlan">Add Plan</base-button>
        </div>
      </div>
    </template>
    <terms-modal ref="termsModal" :selected-plans="selectedPlans" />
  </base-slide-over>
</template>

<script>
import TermsModal from "@/views/business/member-search/components/TermsModal";
import { STATE_OPTIONS } from "@/constants/constants";
import { mapGetters } from "vuex";
import { capitalize, uniq, orderBy } from "lodash";
import { padStart } from "lodash";
import CreditCardIcon from "@/components/CreditCardIcon.vue";
import InlineError from "@/components/InlineError";

export default {
  name: "AddBusinessMemberPlan",
  components: {
    TermsModal,
    CreditCardIcon,
    InlineError,
  },
  props: {
    businessId: String,
    userId: String,
  },
  data() {
    return {
      isFormValid: true,
      isLoadingToken: false,
      isCreatingPlan: false,
      STATE_OPTIONS,
      planStartDate: null,
      isLoadingPlans: false,
      plans: [],
      selectedPlans: [],
      selectedLocationId: null,
      token: null,
      paymentOption: "cardOnFile",
      savedCreditCard: { brand: "", last4: null, expMonth: 1, expYear: 2023 },
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["location", "locations"]),
    title() {
      return `Add ${capitalize(this.organization.customerLabelSingular)} Plan`;
    },
    infoTitle() {
      return `${capitalize(this.organization.customerLabelSingular)} Info`;
    },
    planGroups() {
      return this.plans ? uniq(this.plans.map(({ planGroup }) => planGroup)).sort() : [""];
    },
    planOptions() {
      const planOptions = [];

      this.planGroups.forEach((pg) => {
        planOptions.push({
          name: pg,
          isHeader: true,
        });

        planOptions.push(...orderBy(this.plans.filter((p) => p.planGroup === pg), ["name"]));
      });

      return planOptions;
    },
    locationOptGroups() {
      return STATE_OPTIONS.filter((s) => this.locations.find((l) => l.state === s.value)).map((s) => ({
        label: s.value,
        options: orderBy(this.locations.filter((l) => l.state === s.value), ["name"]),
      }));
    },
    disabledDates() {
      var min = new Date();
      var max = new Date();

      min.setDate(min.getDate() - 1);
      max.setDate(max.getDate() + 30);

      return { to: min, from: max };
    },
  },
  created() {
    this.getPlans();
    this.getCardOnFile();
  },
  mounted() {
    if (this.locations.length === 1) this.selectedLocationId = this.locations[0].businessLocationId;
    this.planStartDate = new Date();
  },
  methods: {
    show() {
      this.$refs.slideOver.show();
      this.$refs.form.reset();
      this.$refs.creditCardInput.reset();
      this.planStartDate = new Date();
    },
    hide() {
      this.error = null;
      this.errorMessage = "";
      this.errorTitle = "";
      this.token = null;
      this.getCardOnFile();
      this.$refs.slideOver.hide();
    },
    async getPlans() {
      try {
        this.isLoadingPlans = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/plans/list`);
        this.plans = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve plans",
          message: "We were not able to retrieve the your business plans. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      } finally {
        this.isLoadingPlans = false;
      }
    },
    getCardOnFile() {
      const url = `/businesses/${this.businessId}/members/${this.userId}/platformcard`;
      this.$axios
        .get(url)
        .then((res) => {
          if (res.data.last4) {
            this.savedCreditCard = res.data;
            this.paymentOption = "cardOnFile";
          } else {
            this.message = "manual";
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    expDate(expMonth, expYear) {
      if (expMonth && expYear) return `${padStart(expMonth, 2, "0")} / ${expYear.toString().slice(-2)}`;
      else return "unknown";
    },
    async showTerms() {
      try {
        this.isLoadingToken = true;
        if (this.paymentOption === "manual") {
          this.token = await this.$refs.creditCardInput.createToken();
          if (!this.$refs.form.validate() || !this.token) return;
        }
        this.$refs.termsModal.show();
      } finally {
        this.isLoadingToken = false;
      }
    },
    async createPlan() {
      if (!this.$refs.form.validate()) return;

      this.isCreatingPlan = true;

      if (this.paymentOption === "manual") {
        this.token = await this.$refs.creditCardInput.createToken();
        if (!this.token) {
          this.isCreatingPlan = false;
          return;
        }
      }

      this.$axios
        .post(`/businesses/${this.businessId}/members/${this.userId}/memberplans`, {
          businessLocationId: this.selectedLocationId,
          businessPlanIds: this.selectedPlans.map((x) => x.businessPlanId),
          startDate: this.planStartDate,
          tokenId: this.token ? this.token.id : null,
        })
        .then((response) => {
          const paymentStatuses = response.data.paymentStatuses;
          const hasSuccessfulOrPendingStartStatus = Object.values(paymentStatuses).some((status) => status === "succeeded" || status === "pending start");

          if (hasSuccessfulOrPendingStartStatus) {
            this.EventBus.$emit("member_update");

            this.EventBus.$emit("GLOBAL_NOTIFY", {
              title: `${capitalize(this.organization.customerLabelSingular)} Created`,
              message: `The ${this.organization.customerLabelSingular} plan was activated`,
            });

            this.hide();
          } else {
            this.errorTitle = `Error activating plan`;
            this.errorMessage = "Sorry, we had trouble activating this plan.";
            this.error = "Error in payment status";
          }
        })
        .catch((error) => {
          this.errorTitle = `Error creating plan`;
          this.errorMessage = "Sorry, we had trouble activating this plan.";
          this.error = error;
        })
        .finally(() => {
          this.isCreatingPlan = false;
        });
    },
  },
};
</script>
